import { useEffect, useMemo, useState } from 'react';

import { CompanyData, SelectedCompany } from '@/modules/auth/helpers/CompanyStorage';
import { getCompanies } from '@/modules/organizations/redux/CompanyCRUD';

type RequestMaterialCompanyOption = { company_id: string; company_name: string };

const normalizeCompanyId = (companyId?: string) => (companyId && companyId !== 'global' ? companyId : '');

const normalizeCompanyOption = (company: any): RequestMaterialCompanyOption => ({
  company_id: company?.company_id || company?.id || '',
  company_name: company?.company_name || company?.name || '',
});

const hasGlobalRole = (globalRoles: any[] = [], selectedCompany?: SelectedCompany) => {
  if (selectedCompany?.company_id === 'global') {
    return true;
  }

  const selectedCompanyRoles = selectedCompany?.roles || [];
  const allRoles = [...globalRoles, ...selectedCompanyRoles];

  return allRoles.some((role) => role?.role_level === 'global' || role?.name === 'global' || role?.role_name === 'global');
};

interface UseRequestMaterialCompanyOptionsParams {
  selectedCompany?: SelectedCompany;
  companies: CompanyData[];
  globalRoles: any[];
  onError: (message: string) => void;
}

export default function useRequestMaterialCompanyOptions({
  selectedCompany,
  companies,
  globalRoles,
  onError,
}: UseRequestMaterialCompanyOptionsParams) {
  const [fallbackCompanies, setFallbackCompanies] = useState<RequestMaterialCompanyOption[]>([]);

  const isGlobal = hasGlobalRole(globalRoles || [], selectedCompany);

  const companyOptions = useMemo(() => {
    const normalizedReduxCompanies = (companies || [])
      .map(normalizeCompanyOption)
      .filter((company: RequestMaterialCompanyOption) => company.company_id && company.company_name);
    const selectedCompanyOption = normalizeCompanyId(selectedCompany?.company_id)
      ? [{ company_id: selectedCompany?.company_id || '', company_name: selectedCompany?.company_name || 'Công ty hiện tại' }]
      : [];

    const merged: RequestMaterialCompanyOption[] = [...normalizedReduxCompanies, ...fallbackCompanies, ...selectedCompanyOption];
    const uniqueCompanies = new Map<string, RequestMaterialCompanyOption>();

    merged.forEach((company: RequestMaterialCompanyOption) => {
      if (company.company_id && !uniqueCompanies.has(company.company_id)) {
        uniqueCompanies.set(company.company_id, company);
      }
    });

    return Array.from(uniqueCompanies.values());
  }, [companies, fallbackCompanies, selectedCompany?.company_id, selectedCompany?.company_name]);

  const ensureCompanyOptionsLoaded = async (): Promise<RequestMaterialCompanyOption[]> => {
    const hasEnoughOptionsForGlobal = !isGlobal || companyOptions.length > 1;
    if (hasEnoughOptionsForGlobal && companyOptions.length > 0) {
      return companyOptions;
    }

    try {
      const response = await getCompanies();
      const payload = Array.isArray(response.data) ? response.data : response.data?.results || [];
      const normalizedCompanies = payload
        .map(normalizeCompanyOption)
        .filter((company: RequestMaterialCompanyOption) => company.company_id && company.company_name);

      setFallbackCompanies(normalizedCompanies);

      if (normalizeCompanyId(selectedCompany?.company_id) && selectedCompany?.company_name) {
        return [
          ...normalizedCompanies,
          { company_id: selectedCompany.company_id, company_name: selectedCompany.company_name },
        ];
      }

      return normalizedCompanies;
    } catch (_error) {
      onError('Không thể tải danh sách công ty cho dropdown.');
      return companyOptions;
    }
  };

  useEffect(() => {
    // For global users, one option often reflects only selected company from storage.
    // Continue loading from API until we have broader company options.
    if (!isGlobal || companyOptions.length > 1) {
      return;
    }

    void ensureCompanyOptionsLoaded();
  }, [companyOptions.length, isGlobal]);

  return {
    isGlobal,
    companyOptions,
    initialCompanyId: normalizeCompanyId(selectedCompany?.company_id) || companyOptions[0]?.company_id || '',
    normalizedSelectedCompanyId: normalizeCompanyId(selectedCompany?.company_id),
    ensureCompanyOptionsLoaded,
  };
}
